import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetCustomersQuery } from "./slices/customerSlice";
import { setCredentials } from "./slices/authSlice";

// The cached list can be reused only if it belongs to the selected outlet
// (customers are per outlet) and is in the current shape, where each record
// has its own _id and the mobile number in mobileNumber
const isCacheUsable = (userInfo) =>
  Boolean(userInfo?.lastCustomersRetrievedTime) &&
  userInfo?.customersCacheOutletId === userInfo?.selectedOutletId &&
  (userInfo?.customersList || []).every(
    (customer) => customer?.mobileNumber !== undefined
  );

// With a usable cache only documents updated since the last fetch are
// requested and merged in; otherwise the outlet's full list is fetched
const buildRequest = (userInfo) => {
  const usable = isCacheUsable(userInfo);
  return {
    outletId: userInfo?.selectedOutletId,
    usable,
    query: usable
      ? `updatedAt[gte]=${encodeURIComponent(
          userInfo.lastCustomersRetrievedTime
        )}`
      : "",
  };
};

// Fetches the selected outlet's customers and keeps a cached copy in redux
// (userInfo.customersList), so repeat visits avoid full get-customers calls.
const useCustomersCache = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const selectedOutletId = userInfo?.selectedOutletId;

  // Decided when the screen opens and again only when the outlet changes,
  // so the timestamp moving forward on merge does not loop the api call
  const [request, setRequest] = useState(() => buildRequest(userInfo));
  useEffect(() => {
    if (request.outletId !== selectedOutletId) {
      setRequest(buildRequest(userInfo));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOutletId]);

  const {
    data: customers,
    isLoading,
    error,
    refetch,
  } = useGetCustomersQuery(
    // outletId only keeps each outlet's response separate in the api cache
    { query: request.query, token: userInfo?.token, outletId: request.outletId },
    { skip: !userInfo?.token }
  );

  useEffect(() => {
    if (!customers) return;
    // A response for the previously selected outlet must not be saved
    if (request.outletId !== userInfo?.selectedOutletId) return;

    const fetched = Array.isArray(customers)
      ? customers
      : customers?.customers || [];

    const merged = new Map(
      (request.usable ? userInfo?.customersList || [] : []).map((customer) => [
        customer._id,
        customer,
      ])
    );
    fetched.forEach((customer) => merged.set(customer._id, customer));

    dispatch(
      setCredentials({
        ...userInfo,
        customersList: Array.from(merged.values()),
        customersCacheOutletId: request.outletId,
        lastCustomersRetrievedTime: new Date().toISOString(),
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, dispatch]);

  return {
    // Never hand out another outlet's (or old-format) cached customers
    customersList: isCacheUsable(userInfo) ? userInfo?.customersList || [] : [],
    isLoading,
    error,
    refetch,
  };
};

export default useCustomersCache;
